import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { authApi } from '@/api/auth';
import { Field } from '@/components/ui/field';
import { ApiError } from '@/lib/api-client';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@/lib/form-schemas';

export function ForgotPasswordPage(): JSX.Element {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
    mode: 'onSubmit',
  });

  const forgot = useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await forgot.mutateAsync(values.email);
      setSentTo(values.email);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Request failed');
    }
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-mb-bg p-6">
      <div className="mb-card p-10 max-w-md w-full">
        <div className="flex flex-col items-center text-center">
          <div className="h-12 w-12 rounded-lg bg-mb-accent-soft text-mb-accent-press flex items-center justify-center mb-4">
            <KeyRound className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <h1 className="font-display text-xl font-bold text-mb-text">
            Reset your password
          </h1>
          <p className="text-sm text-mb-text-muted mt-1">
            Enter your email and we&rsquo;ll send a reset link.
          </p>
        </div>

        {sentTo ? (
          <div className="mt-6 rounded-md border border-mb-border bg-mb-surface p-4 text-sm text-mb-text-muted">
            If an account exists for{' '}
            <span className="font-mono text-mb-text">{sentTo}</span>, you&rsquo;ll get a
            reset email within a minute. The link expires in 1 hour. Check spam if it
            doesn&rsquo;t arrive.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-3" noValidate>
            <Field label="Email" required error={form.formState.errors.email?.message}>
              <input
                type="email"
                autoComplete="email"
                className="mb-input"
                placeholder="you@yourcompany.com"
                {...form.register('email')}
              />
            </Field>
            {submitError ? (
              <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
                {submitError}
              </div>
            ) : null}
            <button
              type="submit"
              disabled={forgot.isPending || form.formState.isSubmitting}
              className="mb-btn-primary w-full"
            >
              {forgot.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Send reset link
            </button>
          </form>
        )}

        <p className="text-xs text-mb-text-muted text-center mt-6">
          Remembered it?{' '}
          <Link to="/login" className="text-mb-accent-press hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
